import React, { useState, Fragment, useEffect, useCallback } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, PlusCircleIcon } from '@heroicons/react/20/solid';
import { Proposal, ProposalResource } from '../../hooks/useProposals';
import ResourceInput from './ResourceInput';
import Thermometer from './Thermometer';
import { User } from 'firebase/auth';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useDropzone } from 'react-dropzone';

// Placeholder Client interface - ideally this comes from a types file or useClients hook
interface Client {
    id: string;
    name: string;
}

// Special type for the "Create new" option in ComboBox
interface CreateNewClientOption {
    id: string; // Needs a unique ID for Combobox.Option key, e.g., `__CREATE__${query}`
    name: string; // This will be the clientQuery
    createNew: true;
}

export type DurationUnit = 'weeks' | 'months';

interface Duration {
    value: number;
    unit: DurationUnit;
}

// Extracting HeatLevel type from Proposal for clarity
type ProposalHeatLevel = Proposal['heatLevel'];
// Extracting Status type from Proposal for clarity
type ProposalStatus = Proposal['status'];

interface ProposalFormProps {
    initialProposal?: Omit<Proposal, 'id' | 'createdAt' | 'updatedAt'> | null;
    user: User | null;
    isEditing: boolean;
    onSubmit: (
        proposalData: Omit<Proposal, 'id' | 'createdAt' | 'updatedAt' | 'clientId'> & { clientId?: string },
        newClientName?: string
    ) => Promise<void>;
    onCancel: () => void;
    loadingResourceOptions?: boolean;
    loadingResourceDetails?: boolean;
    clients?: Client[];
    loadingClients?: boolean;
}

export default function ProposalForm({
    initialProposal,
    user,
    isEditing,
    onSubmit,
    onCancel,
    loadingResourceOptions,
    loadingResourceDetails,
    clients = [],
    loadingClients,
}: ProposalFormProps) {
    const [currentProposal, setCurrentProposal] = useState<Omit<Proposal, 'id' | 'createdAt' | 'updatedAt'>>({
        name: '',
        clientId: '',
        duration: '',
        expectedClosing: '',
        proposalDate: '',
        heatLevel: 'cool',
        status: 'negotiating',
        resources: [],
        manager: user?.displayName || '',
        notes: '',
        proposalUrl: initialProposal?.proposalUrl || '',
    });
    const [duration, setDuration] = useState<Duration>({ value: 1, unit: 'months' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);
    const [isInitializing, setIsInitializing] = useState(true);

    // State for ComboBox
    const [clientQuery, setClientQuery] = useState('');
    // showNewClientForm is no longer needed as ComboBox handles creation intent
    const [newClientName, setNewClientName] = useState(''); // Used if "Create new" is selected in ComboBox
    const [selectedClientForCombobox, setSelectedClientForCombobox] = useState<Client | CreateNewClientOption | null>(null);

    const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
        setSubmitError(null);
        if (rejectedFiles && rejectedFiles.length > 0) {
            if (rejectedFiles[0].errors && rejectedFiles[0].errors[0].code === 'file-too-large') {
                setSubmitError("Arquivo muito grande. O limite é de X MB.");
            } else if (rejectedFiles[0].errors && rejectedFiles[0].errors[0].code === 'file-invalid-type') {
                setSubmitError("Tipo de arquivo inválido. Por favor, selecione um arquivo PDF.");
            } else if (rejectedFiles[0].errors && rejectedFiles[0].errors[0].code === 'too-many-files') {
                setSubmitError("Por favor, envie apenas um arquivo.");
            } else {
                setSubmitError("Não foi possível aceitar o arquivo. Verifique se é um PDF válido.");
            }
            setSelectedFile(null);
            return;
        }

        if (acceptedFiles && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            if (file.type === "application/pdf") {
                setSelectedFile(file);
                setUploadProgress(null);
            } else {
                setSubmitError("Tipo de arquivo inválido. Por favor, selecione um arquivo PDF.");
                setSelectedFile(null);
            }
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
        onDrop,
        accept: { 'application/pdf': ['.pdf'] },
        multiple: false,
    });

    useEffect(() => {
        setIsInitializing(true);
        if (isEditing && initialProposal) {
            const rawStatus = initialProposal.status.split('//')[0].trim(); // Clean the status
            const validStatus = ['negotiating', 'won', 'lost', 'cancelled'].includes(rawStatus)
                ? rawStatus as ProposalStatus
                : 'negotiating';
            const validHeatLevel = ['frozen', 'cold', 'cool', 'warm', 'hot', 'burning'].includes(initialProposal.heatLevel)
                ? initialProposal.heatLevel as ProposalHeatLevel
                : 'cool';

            setCurrentProposal({ ...initialProposal, status: validStatus, heatLevel: validHeatLevel });
            const durationMatch = initialProposal.duration.match(/(\d+)\s+(semanas|meses)/);
            if (durationMatch) {
                setDuration({
                    value: parseInt(durationMatch[1]),
                    unit: durationMatch[2] === 'semanas' ? 'weeks' : 'months'
                });
            }
            setNewClientName(''); // Clear any new client name when editing
            setClientQuery(''); // Clear query
        } else {
            setCurrentProposal({
                name: '',
                clientId: '',
                duration: '',
                expectedClosing: '',
                proposalDate: '',
                heatLevel: 'cool',
                status: 'negotiating',
                resources: [],
                manager: user?.displayName || '',
                notes: '',
                proposalUrl: '',
            });
            setDuration({ value: 1, unit: 'months' });
            setNewClientName('');
            setClientQuery('');
        }
        if (initialProposal?.proposalUrl && (!isEditing || (isEditing && initialProposal.proposalUrl))) {
            setSelectedFile(null);
            setUploadProgress(null);
        }
        setIsInitializing(false);
    }, [isEditing, initialProposal, user]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError(null);

        if (!currentProposal.name.trim()) {
            setSubmitError("O nome da proposta é obrigatório.");
            setIsSubmitting(false);
            return;
        }

        // Validation for client: either clientId is set, or newClientName is set (and not editing)
        if (!isEditing && !currentProposal.clientId && !newClientName.trim()) {
            setSubmitError("O cliente é obrigatório. Selecione um existente ou digite um novo nome para criar.");
            setIsSubmitting(false);
            return;
        }
        if (isEditing && !currentProposal.clientId) {
            setSubmitError("O cliente é obrigatório para propostas existentes.");
            setIsSubmitting(false);
            return;
        }

        let fileURL = currentProposal.proposalUrl || '';
        if (selectedFile) {
            setUploadProgress(0);
            const storage = getStorage();
            const safeFileName = selectedFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
            const storageRef = ref(storage, `proposals/${Date.now()}_${safeFileName}`);
            const uploadTask = uploadBytesResumable(storageRef, selectedFile);
            try {
                await new Promise<void>((resolve, reject) => {
                    uploadTask.on('state_changed',
                        (snapshot) => {
                            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            setUploadProgress(progress);
                        },
                        (error) => {
                            console.error("Upload failed:", error);
                            setSubmitError("Falha no upload do arquivo: " + error.message);
                            reject(error);
                        },
                        async () => {
                            fileURL = await getDownloadURL(uploadTask.snapshot.ref);
                            resolve();
                        }
                    );
                });
            } catch (error) {
                setIsSubmitting(false);
                return;
            }
        }

        try {
            const { clientId, ...restOfProposal } = currentProposal;
            const proposalDataToSubmit = {
                ...restOfProposal,
                duration: `${duration.value} ${duration.unit === 'weeks' ? 'semanas' : 'meses'}`,
                proposalUrl: fileURL,
            };

            if (newClientName.trim() && !isEditing) {
                await onSubmit(proposalDataToSubmit, newClientName.trim());
            } else {
                await onSubmit({ ...proposalDataToSubmit, clientId: currentProposal.clientId });
            }

            setSelectedFile(null);
            setUploadProgress(null);
            setClientQuery('');
            setNewClientName('');
        } catch (err) {
            console.error('Erro ao salvar proposta:', err);
            setSubmitError(err instanceof Error ? err.message : 'Erro desconhecido ao salvar proposta');
        } finally {
            setIsSubmitting(false);
        }
    };

    const filteredClientsForCombobox = clientQuery === ''
        ? clients
        : clients.filter(client =>
            client.name.toLowerCase().includes(clientQuery.toLowerCase())
        );

    const showCreateNewClientOption = !isEditing && clientQuery.trim() !== '' && !filteredClientsForCombobox.some(c => c.name.toLowerCase() === clientQuery.toLowerCase().trim());

    const selectedClientForDisplay = clients.find(c => c.id === currentProposal.clientId);

    const formatDateForInput = (dateStr: string | undefined) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toISOString().split('T')[0];
        } catch (e) {
            return '';
        }
    };

    const dropzoneBaseStyle = "mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md cursor-pointer transition-colors duration-200 ease-in-out";
    const dropzoneActiveStyle = "border-indigo-500 bg-indigo-50";
    const dropzoneAcceptStyle = "border-green-500 bg-green-50";
    const dropzoneRejectStyle = "border-red-500 bg-red-50";

    return (
        <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                    {isEditing ? 'Editar Proposta' : 'Nova Proposta'}
                </h3>
                {isInitializing ? (
                    <div className="mt-5 text-center text-gray-500">
                        Carregando dados da proposta...
                    </div>
                ) : (
                    <>
                        {submitError && (
                            <div className="mt-2 p-3 bg-red-100 border border-red-300 rounded text-red-700">
                                {submitError}
                            </div>
                        )}
                        <form onSubmit={handleSubmit} className="mt-5 space-y-6">
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="proposalName" className="block text-sm font-medium text-gray-700">Nome da Proposta</label>
                                    <input
                                        id="proposalName"
                                        type="text"
                                        value={currentProposal.name}
                                        onChange={(e) => setCurrentProposal({ ...currentProposal, name: e.target.value })}
                                        className="input-primary mt-1"
                                        required
                                        disabled={isSubmitting || isInitializing}
                                    />
                                </div>
                                <div>
                                    <Combobox
                                        as="div"
                                        className="relative w-full"
                                        value={selectedClientForCombobox}
                                        onChange={(selection: Client | CreateNewClientOption | null) => {
                                            setSubmitError(null);
                                            setSelectedClientForCombobox(selection);

                                            if (selection && 'createNew' in selection && selection.createNew) {
                                                setCurrentProposal({ ...currentProposal, clientId: '' });
                                                setNewClientName(selection.name);
                                                setClientQuery(selection.name);
                                            } else if (selection && 'id' in selection && !('createNew' in selection)) {
                                                setCurrentProposal({ ...currentProposal, clientId: selection.id });
                                                setNewClientName('');
                                                setClientQuery(selection.name);
                                            } else {
                                                setCurrentProposal({ ...currentProposal, clientId: '' });
                                            }
                                        }}
                                        disabled={isSubmitting || isInitializing || loadingClients}
                                        nullable
                                    >
                                        <Combobox.Label className="block text-sm font-medium text-gray-700">Cliente</Combobox.Label>
                                        <div className="relative mt-1">
                                            <Combobox.Input
                                                className="input-primary w-full pr-10"
                                                displayValue={(item: Client | CreateNewClientOption | null) => item?.name || clientQuery}
                                                onChange={(event) => {
                                                    const newQuery = event.target.value;
                                                    setClientQuery(newQuery);

                                                    if (selectedClientForCombobox) {
                                                        setSelectedClientForCombobox(null);
                                                    }
                                                    setCurrentProposal({ ...currentProposal, clientId: '' });

                                                    const isPotentialNewClient = !isEditing && newQuery.trim() !== '' &&
                                                        !clients.find(c => c.name.toLowerCase() === newQuery.toLowerCase().trim());

                                                    if (isPotentialNewClient) {
                                                        setNewClientName(newQuery.trim());
                                                    } else {
                                                        setNewClientName('');
                                                    }
                                                    setSubmitError(null);
                                                }}
                                                placeholder="Digite para buscar ou criar cliente"
                                                autoComplete="off"
                                            />
                                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                                            </Combobox.Button>
                                        </div>
                                        <Transition
                                            as={Fragment}
                                            leave="transition ease-in duration-100"
                                            leaveFrom="opacity-100"
                                            leaveTo="opacity-0"
                                            afterLeave={() => setClientQuery('')}
                                        >
                                            <Combobox.Options
                                                className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                                            >
                                                {loadingClients && clientQuery === '' && !selectedClientForCombobox && (
                                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">Carregando clientes...</div>
                                                )}
                                                {filteredClientsForCombobox.length === 0 && clientQuery !== '' && !showCreateNewClientOption && !loadingClients && (
                                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">Nenhum cliente encontrado.</div>
                                                )}
                                                {filteredClientsForCombobox.map((client) => (
                                                    <Combobox.Option
                                                        key={client.id}
                                                        className={({ active }) => `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                                                        value={client}
                                                    >
                                                        {({ selected, active }) => (
                                                            <>
                                                                <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>{client.name}</span>
                                                                {selected ? (
                                                                    <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}><CheckIcon className="h-5 w-5" aria-hidden="true" /></span>
                                                                ) : null}
                                                            </>
                                                        )}
                                                    </Combobox.Option>
                                                ))}
                                                {showCreateNewClientOption && (
                                                    <Combobox.Option
                                                        key={`__CREATE__${clientQuery}`}
                                                        value={{ id: `__CREATE__${clientQuery}`, name: clientQuery, createNew: true } as CreateNewClientOption}
                                                        className={({ active }) => `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`}
                                                    >
                                                        {({ active }) => (
                                                            <span className="flex items-center w-full">
                                                                <PlusCircleIcon className={`mr-2 h-5 w-5 ${active ? 'text-white' : 'text-indigo-600'}`} />
                                                                <span className="truncate">
                                                                    Criar novo cliente: "<span className="font-semibold">{clientQuery}</span>"
                                                                </span>
                                                            </span>
                                                        )}
                                                    </Combobox.Option>
                                                )}
                                            </Combobox.Options>
                                        </Transition>
                                    </Combobox>
                                    {isEditing && <p className="mt-1 text-xs text-gray-500">A edição de cliente via este campo não cria um novo. Selecione um cliente existente.</p>}
                                </div>
                                <div>
                                    <label htmlFor="manager" className="block text-sm font-medium text-gray-700">Responsável</label>
                                    <input
                                        id="manager"
                                        type="text"
                                        value={currentProposal.manager}
                                        onChange={(e) => setCurrentProposal({ ...currentProposal, manager: e.target.value })}
                                        className="input-primary mt-1"
                                        required
                                        disabled={isSubmitting || isInitializing}
                                        placeholder="Nome do responsável pela proposta"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="proposalDate" className="block text-sm font-medium text-gray-700">Data da Proposta</label>
                                    <input
                                        id="proposalDate"
                                        type="date"
                                        value={formatDateForInput(currentProposal.proposalDate)}
                                        onChange={(e) => setCurrentProposal({ ...currentProposal, proposalDate: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                                        className="input-primary mt-1"
                                        disabled={isSubmitting || isInitializing}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="expectedClosing" className="block text-sm font-medium text-gray-700">Previsão de Fechamento</label>
                                    <input
                                        id="expectedClosing"
                                        type="date"
                                        value={formatDateForInput(currentProposal.expectedClosing)}
                                        onChange={(e) => setCurrentProposal({ ...currentProposal, expectedClosing: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                                        className="input-primary mt-1"
                                        disabled={isSubmitting || isInitializing}
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="durationValue" className="block text-sm font-medium text-gray-700">Duração Estimada</label>
                                    <div className="mt-1 flex rounded-md shadow-sm">
                                        <input
                                            id="durationValue"
                                            type="number"
                                            min="1"
                                            value={duration.value}
                                            onChange={(e) => setDuration({ ...duration, value: parseInt(e.target.value) || 1 })}
                                            className="input-primary rounded-r-none"
                                            required
                                            disabled={isSubmitting || isInitializing}
                                        />
                                        <select
                                            id="durationUnit"
                                            value={duration.unit}
                                            onChange={(e) => setDuration({ ...duration, unit: e.target.value as DurationUnit })}
                                            className="input-primary rounded-l-none border-l-0"
                                            disabled={isSubmitting || isInitializing}
                                        >
                                            <option value="weeks">Semanas</option>
                                            <option value="months">Meses</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Termômetro (Heat Level)</label>
                                <Thermometer
                                    heatLevel={currentProposal.heatLevel}
                                    onHeatLevelChange={(level: ProposalHeatLevel) => setCurrentProposal({ ...currentProposal, heatLevel: level })}
                                    disabled={isSubmitting || isInitializing}
                                />
                            </div>

                            <div className="sm:col-span-2">
                                <label htmlFor="status" className="block text-sm font-medium text-gray-700">Status</label>
                                <select
                                    id="status"
                                    name="status"
                                    value={currentProposal.status}
                                    onChange={(e) => setCurrentProposal({ ...currentProposal, status: e.target.value as ProposalStatus })}
                                    className="input-primary mt-1"
                                    required
                                    disabled={isSubmitting || isInitializing}
                                >
                                    <option value="negotiating">Em Negociação</option>
                                    <option value="won">Ganha</option>
                                    <option value="lost">Perdida</option>
                                    <option value="cancelled">Cancelada</option>
                                </select>
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Arquivo da Proposta (PDF)
                                </label>
                                <div
                                    {...getRootProps()}
                                    className={`
                                        ${dropzoneBaseStyle}
                                        ${isDragActive ? dropzoneActiveStyle : ''}
                                        ${isDragAccept ? dropzoneAcceptStyle : ''}
                                        ${isDragReject ? dropzoneRejectStyle : ''}
                                    `}
                                >
                                    <input {...getInputProps()} />
                                    <div className="text-center">
                                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        {selectedFile ? (
                                            <p className="mt-1 text-sm text-gray-600">
                                                Arquivo selecionado: <span className="font-medium">{selectedFile.name}</span>
                                            </p>
                                        ) : isDragActive ? (
                                            <p className="mt-1 text-sm text-gray-600">Solte o arquivo aqui...</p>
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-600">
                                                Arraste e solte um arquivo PDF aqui, ou <span className="font-medium text-indigo-600 hover:text-indigo-500">clique para selecionar</span>.
                                            </p>
                                        )}
                                        {currentProposal.proposalUrl && !selectedFile && (
                                            <p className="mt-1 text-xs text-gray-500">
                                                Proposta atual: <a href={currentProposal.proposalUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">{currentProposal.proposalUrl.split('/').pop()?.split('?')[0].substring(14)}</a>
                                            </p>
                                        )}
                                    </div>
                                </div>
                                {uploadProgress !== null && selectedFile && (
                                    <div className="mt-2">
                                        <div className="text-sm text-gray-500">Upload: {Math.round(uploadProgress)}%</div>
                                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                                            <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="sm:col-span-2">
                                <ResourceInput
                                    resources={currentProposal.resources}
                                    onChange={(resources: ProposalResource[]) => setCurrentProposal({ ...currentProposal, resources })}
                                    disabled={isSubmitting || isInitializing}
                                />
                            </div>

                            <div className="sm:col-span-2">
                                <label htmlFor="notes" className="block text-sm font-medium text-gray-700">Observações</label>
                                <textarea
                                    id="notes"
                                    name="notes"
                                    rows={4}
                                    value={currentProposal.notes || ''}
                                    onChange={(e) => setCurrentProposal({ ...currentProposal, notes: e.target.value })}
                                    className="input-primary mt-1"
                                    disabled={isSubmitting || isInitializing}
                                />
                            </div>

                            <div className="pt-5">
                                <div className="flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={onCancel}
                                        className="btn-secondary"
                                        disabled={isSubmitting}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-primary"
                                        disabled={isSubmitting || isInitializing || (loadingClients && !newClientName.trim() && !currentProposal.clientId && !selectedClientForCombobox)}
                                    >
                                        {isSubmitting ? 'Salvando...' : (isEditing ? 'Salvar Alterações' : 'Criar Proposta')}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
} 